import { Box, Stack, Typography } from "@mui/material";
import { gridClasses } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useState } from "react";
import { cenikModel } from "../../../api/models/cenikModel";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { CenikItem } from "../../../ts/Cenik";
import Button from "../../ui/Button/Button";
import { SMALL_ITEMS_PER_PAGE } from "../../ui/LazyDataGrid/common";
import { DataGrid } from "../../ui/LazyDataGrid/DataGrid";
import { LazyDataGrid } from "../../ui/LazyDataGrid/LazyDataGrid";
import { TCenikQuery } from "./Cenik";
import { mapCenikToCenikItems, TCenikCombinationItem } from "./utils";

interface IProps {
  cenikQuery: TCenikQuery | null;
  onSelectRow?: (row: CenikItem) => void;
}

export const CenikPolozky = ({
  cenikQuery,
  onSelectRow,
}: IProps): React.ReactElement<IProps> => {
  const { call } = useQueryWithPopupErrorHandling();
  const [data, setData] = useState<TCenikCombinationItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(SMALL_ITEMS_PER_PAGE);
  const [lastRefreshDate, setLastRefreshDate] = useState<number>(0);

  const getCenik = useCallback(async () => {
    setIsLoading(true);
    await call(
      () =>
        cenikModel.getFilteredCenik({
          offset: (page * pageSize).toString(),
          limit: pageSize.toString(),
          query: cenikQuery ? cenikQuery.query : undefined,
          sirka: cenikQuery ? cenikQuery.sirka : undefined,
          vyska: cenikQuery ? cenikQuery.vyska : undefined,
          kod: cenikQuery ? cenikQuery.kod : undefined,
        }),
      "Zobrazení ceníka nebolo úspešné",
      null,
      ({ data, totalCount }) => {
        setData(mapCenikToCenikItems(data));
        setTotalCount(totalCount);
      },
      undefined,
      true
    );
    setIsLoading(false);
  }, [page, pageSize, cenikQuery]);

  useEffect(() => {
    getCenik();
  }, [getCenik]);

  const getLastRefreshDate = useCallback(async () => {
    await call(
      () => cenikModel.getLastRefreshDate(),
      null,
      null,
      (lastRefreshDate) => {
        setLastRefreshDate(lastRefreshDate);
      },
      undefined,
      true
    );
  }, []);

  useEffect(() => {
    getLastRefreshDate();
  }, [getLastRefreshDate]);

  const refreshCenik = useCallback(async () => {
    await call(
      () => cenikModel.refreshCenik(),
      "Aktualizovanie cenníka nebolo úspešné",
      null,
      () => {
        getCenik();
        getLastRefreshDate();
      }
    );
  }, [getCenik]);

  return (
    <Box
      sx={{
        ".MuiDataGrid-row:hover": { background: "#fff !important" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        justifyContent="end"
        mb={1.5}
      >
        {!lastRefreshDate || lastRefreshDate <= 0 ? (
          <Box fontSize={11} fontWeight={500}>
            CENNÍK NEBOL AKTUALIZOVANÝ
          </Box>
        ) : (
          <Box fontSize={11} fontWeight={500}>
            NAPOSLEDY AKTUALIZOVANÝ {new Date(lastRefreshDate).toLocaleString()}
          </Box>
        )}
        <Button style={{ fontSize: 13 }} onClick={refreshCenik}>
          AKTUALIZOVAŤ CENNÍK
        </Button>
      </Stack>
      <LazyDataGrid<TCenikCombinationItem>
        id="cenik"
        rows={data}
        pageSizeInitial={SMALL_ITEMS_PER_PAGE}
        isLoading={isLoading}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowHeight={() => "auto"}
        columns={[
          {
            field: "name",
            headerName: "Názov a kombinácie",
            flex: 1,
            renderCell: ({ row: parentRow }) => {
              return (
                <Stack gap={1}>
                  <Typography fontSize={14} fontWeight={500} mt={2}>
                    {parentRow.name}
                  </Typography>
                  <Box
                    sx={{
                      mb: 1,
                      [`& .${gridClasses.columnHeader}`]: {
                        backgroundColor: "#fff",
                        color: "#000 !important",
                        "*": {
                          color: "#000 !important",
                        },
                      },
                      [`& .${gridClasses.footerContainer}`]: {
                        backgroundColor: "#fff",
                      },
                      "& .MuiTablePagination-root": {
                        color: "#000 !important",

                        "& .MuiSvgIcon-root": {
                          color: "#000 !important",
                        },
                      },
                      ".MuiDataGrid-row:hover": {
                        background: "#33333309 !important",
                      },
                    }}
                  >
                    <DataGrid<CenikItem>
                      id="cenik-items"
                      hidePageSizeText
                      pageSizeInitial={SMALL_ITEMS_PER_PAGE}
                      onRowClick={onSelectRow}
                      columns={[
                        {
                          field: "kod",
                          headerName: "Kód",
                          width: 250,
                        },
                        {
                          field: "sirka",
                          headerName: "Šírka",
                          width: 115,
                        },
                        {
                          field: "vyska",
                          headerName: "Výška",
                          width: 115,
                        },
                        {
                          field: "dokoncena_stena",
                          headerName: "Dokončená stena",
                          width: 150,
                        },
                        {
                          field: "popis",
                          headerName: "Popis",
                          width: 300,
                        },
                        {
                          field: "mj",
                          headerName: "Merná jednotka",
                          width: 115,
                        },
                        {
                          field: "cennikova_cena",
                          headerName: "Cena",
                          minWidth: 110,
                          flex: 1,
                        },
                      ]}
                      rows={parentRow.items.filter((item) => {
                        if (cenikQuery?.kod && cenikQuery.kod.length > 0) {
                          return item.kod
                            .toLowerCase()
                            .trim()
                            .includes(cenikQuery.kod.toLowerCase().trim());
                        }

                        return item;
                      })}
                    />
                  </Box>
                </Stack>
              );
            },
          },
        ]}
      />
    </Box>
  );
};
